import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';

interface Offer {
  id: string;
  name: string;
  offerType: string;
  slaCategory: string;
  environmentType: string | null;
  description: string | null;
}


interface ServicePlan {
  id: string;
  name: string;
  type: string;
  startDate: string;
  endDate: string;
  price: number;
  priceEffectiveDate: string;
  inactive: boolean;
  isTaxable: boolean;
  planProvisioningDate: string | null;
  offer: Offer;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const PlanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDate, formatDateTime } = useDateFormatter();
  const navigate = useNavigate();
  const { canViewEntity, canUpdateEntity, loading: permissionsLoading } = useUserPermissions();
  const [plan, setPlan] = useState<ServicePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user can access service plans
  const canAccessServicePlans = canViewEntity('service_plans');
  const canEditServicePlans = canUpdateEntity('service_plans');

  const fetchPlan = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await api.get(`/service-plans/${id}`);
      console.log('API Response:', response.data);
      console.log('Offer object:', response.data?.Offer || response.data?.offer);
      setPlan(response.data);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch service plan';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    // Don't redirect while permissions are still loading
    if (permissionsLoading) {
      return;
    }

    if (!canAccessServicePlans) {
      navigate('/dashboard');
      return;
    }
    if (id) {
      fetchPlan();
    }
  }, [canAccessServicePlans, navigate, id, permissionsLoading, fetchPlan]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'service_fee':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'service_flat':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Show loading while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="ml-4 text-gray-600">Loading permissions...</p>
      </div>
    );
  }

  if (!canAccessServicePlans) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-sm text-gray-700">
            You do not have permission to view service plan details.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error || 'Service plan not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link
          to="/service-plans"
          className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          ← Back to Service Plans
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">{plan.name}</h1>
          {canEditServicePlans && (
            <div className="flex gap-2">
              <Link
                to={`/service-plans/${plan.id}/edit`}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
              >
                Edit
              </Link>
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Plan Information</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Plan Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{plan.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Type</dt>
                  <dd className="mt-1">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getTypeBadgeColor(plan.type)}`}>
                      {plan.type.replace('_', ' ')}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      !plan.inactive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {!plan.inactive ? (
                        <>
                          <CheckCircleIcon className="w-3 h-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="w-3 h-3 mr-1" />
                          Inactive
                        </>
                      )}
                    </span>
                    {plan.isTaxable && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Taxable
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Associated Offer</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Offer Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{plan.offer?.name || 'No offer data'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Offer Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">{plan.offer?.offerType?.replace('_', ' ') || 'No offer type'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">SLA Category</dt>
                  <dd className="mt-1 text-sm text-gray-900">{plan.offer?.slaCategory || 'No SLA category'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Environment Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">{plan.offer?.environmentType || 'Not specified'}</dd>
                </div>
                {plan.offer?.description && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900">{plan.offer.description}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Details</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Price</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-bold text-lg">{formatCurrency(plan.price)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Price Effective Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(plan.priceEffectiveDate)}</dd>
                </div>
              </dl>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(plan.startDate)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">End Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(plan.endDate)}</dd>
                </div>
                {plan.planProvisioningDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Plan Provisioning Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(plan.planProvisioningDate)}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Audit Information</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDateTime(plan.createdAt)}</dd>
                </div>
                {plan.createdBy && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created By</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {plan.createdBy.firstName} {plan.createdBy.lastName}
                      <div className="text-xs text-gray-500">{plan.createdBy.email}</div>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDateTime(plan.updatedAt)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanDetail;